import { useMemo } from "react";
import { useUser, useChatSelection } from "@platform/store";
import {
  useChat,
  useChatActions,
  useChatProgramId,
  useTalkJSData,
} from "@platform/hooks/useChat";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import { useFetchUserCircles } from "@core/features/circle";
import {
  useResolvedChatCircle,
  useChatUnreadConversationIds,
} from "@core/features/chat";
import { ChatSheet } from "./ChatSheet";

export function ChatSheetContainer() {
  const { isOpen } = useChat();
  const { closeChat } = useChatActions();
  const talkjsData = useTalkJSData();
  const user = useUser();
  const programId = useChatProgramId();
  const { data: circlesData } = useFetchUserCircles({ user });
  const userCircles = circlesData?.circles ?? null;

  const { shouldShowChat, isLoading: isChatStatusLoading } = useChatVisibility({
    userCircles,
  });

  const { selectedCircleByProgram, setSelectedCircle, clearSelectedCircle } =
    useChatSelection();

  // Resolve which circle's conversation should render in the dashboard
  // surface. The hook handles auto-select for single-circle users,
  // honors a persisted selection when valid, and falls back to the
  // picker UI when needed. Stale selections are evicted as a side
  // effect so the store doesn't accumulate dead entries.
  const resolved = useResolvedChatCircle({
    userCircles,
    programId,
    talkjsData,
    selectedCircleByProgram,
    clearSelectedCircle,
  });

  // Per-conversation unread state for the switcher's dots. Scoped to
  // the active program's conversations so unread state from other
  // programs (which the dashboard isn't currently surfacing) doesn't
  // light up dots in this sheet.
  const allowedConversationIds = useMemo(
    () =>
      talkjsData?.conversations.map((c) => c.talkjs_conversation_id) ?? [],
    [talkjsData]
  );
  const unreadConversationIds = useChatUnreadConversationIds(
    allowedConversationIds
  );

  // Don't render chat sheet if chat is disabled or missing required data
  if (!isChatStatusLoading && !shouldShowChat) {
    return null;
  }

  // Don't render if required data is missing
  if (!talkjsData || !user) {
    return null;
  }

  const handleSelectCircle = (circleId: number) => {
    if (programId != null) {
      setSelectedCircle(programId, circleId);
    }
  };

  return (
    <ChatSheet
      isLoading={isChatStatusLoading}
      isOpen={isOpen}
      onClose={closeChat}
      talkjsData={talkjsData}
      user={user}
      resolved={resolved}
      unreadConversationIds={unreadConversationIds}
      onSelectCircle={handleSelectCircle}
    />
  );
}

export default ChatSheetContainer;
